// charts/WaterfallChart.jsx
import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * WaterfallChart
 * --------------
 * Minimal wrapper around Highcharts Waterfall that matches your API style.
 *
 * Props:
 *   data: Array<{ label: string, value: number, kind?: 'subtotal'|'total', color?: string }>
 *   legend: 'right' | 'bottom' | null/undefined (hidden when null/undefined)
 *   horizontal: boolean (false = vertical, true = horizontal/inverted)
 *   dataLabels: boolean
 *   upColor?: string      // optional override for positive bars
 *   downColor?: string    // optional override for negative bars
 *   sumColor?: string     // optional override for 'total'/'subtotal' bars
 *
 * Notes:
 * - Stable series id and point ids for smooth animations on data updates.
 * - Theme-aware axes and connectors using MUI divider color.
 * - Background stays transparent; BaseHighchart handles sizing, font, and theme text colors.
 */
export const WaterfallChart = ({
  data = [],
  legend,
  horizontal = false,
  dataLabels = false,
  upColor,
  downColor,
  sumColor,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;

  if (!data.length) return null;

  // Build object points with stable ids; mark totals/subtotals
  const points = data.map(item => ({
    id: String(item.label),                 // stable identity helps animations
    name: item.label,
    y: item.value,                          // ignored for totals/subtotals by HC, but harmless
    isIntermediateSum: item.kind === 'subtotal',
    isSum: item.kind === 'total',
    color: item.color,                      // optional per-point override
  }));

  // Defaults if not provided
  const positiveColor = upColor ?? theme.palette.success.main;
  const negativeColor = downColor ?? theme.palette.error.main;
  const totalColor    = sumColor ?? theme.palette.text.secondary;

  const legendOptions =
    legend == null
      ? { enabled: false }
      : legend === 'bottom'
        ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
        : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const options = {
    chart: {
      type: 'waterfall',
      inverted: !!horizontal,         // horizontal bars when true
      spacing: [8, 8, 24, 8],
      animation: { duration: 300 },
    },
    title: { text: null },
    credits: { enabled: false },
    legend: legendOptions,

    // Category labels come from point.name
    xAxis: {
      type: 'category',
      tickLength: 0,
      lineWidth: 1,
      lineColor: dividerColor,        // solid baseline
      gridLineWidth: 0,               // gridlines from yAxis only
    },
    yAxis: {
      title: { text: null },
      lineWidth: 0,
      gridLineWidth: 1,
      gridLineColor: dividerColor,    // faint gridlines
      gridLineDashStyle: 'ShortDot',
    },

    tooltip: {
      pointFormat: '<b>{point.y:,.0f}</b>',
      shared: false,
    },

    plotOptions: {
      series: {
        animation: { duration: 300 },
        borderWidth: 0,               // clean look; card provides chrome
        turboThreshold: 0,            // allow object points with ids
        dataLabels: {
          enabled: dataLabels,
          padding: 2,
        },
      },
      waterfall: {
        upColor: positiveColor,       // positive deltas
        color: negativeColor,         // negative deltas
        pointPadding: 0.08,
        // Connector “bridges” between columns use theme divider
        connectorWidth: 1,
        connectorColor: dividerColor,
        // (Hover halos off for a flatter look—optional)
        states: { hover: { halo: { size: 0 } } },
      },
    },

    series: [
      {
        id: 'waterfall',              // stable series id for smoother updates
        type: 'waterfall',
        name: 'Value',
        // If a point is total/subtotal and no explicit color was given, use totalColor
        data: points.map(p =>
          (p.isSum || p.isIntermediateSum) && !p.color ? { ...p, color: totalColor } : p
        ),
        showInLegend: !!legendOptions.enabled,
      },
    ],
  };

  return <BaseHighchart options={options} />;
};
